import React, { useEffect, useState, useRef } from 'react';
import { Container, Box, Typography, Grid, Skeleton, Card, CardContent } from '@mui/material';
import ProductCard from '../product/ProductCard';
import { search } from '@api/search';

// Product card skeleton for FeaturedProducts
function FeaturedProductSkeleton() {
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Skeleton variant="rectangular" height={200} sx={{ borderRadius: '4px 4px 0 0' }} />
      <CardContent sx={{ flexGrow: 1, p: 2 }}>
        <Skeleton variant="text" height={24} sx={{ mb: 1 }} />
        <Skeleton variant="text" height={20} width="60%" sx={{ mb: 1 }} />
        <Skeleton variant="text" height={28} width="40%" sx={{ mt: 2 }} />
      </CardContent>
    </Card>
  );
}

const FeaturedProducts = React.memo(() => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    
    const loadFeaturedProducts = async () => {
      if (mountedRef.current) {
        setLoading(true);
        setError(null);
      }
      
      try {
        const result = await search({
          query: '*:*',
          rows: 8,
          queryProfile: 'landing-page',
          collapse: true,
          collapseField: 'group',
          expand: true,
          expandField: 'image_url'
        });
        
        if (!mountedRef.current) return;
        
        if (result.success) {
          // Merge expanded image variants into each product
          const expanded = result.data?.expanded || {};
          
          const productsWithVariants = (result.documents || []).map(product => {
            const groupId = product.group || product.id;
            const expandedDocs = expanded[groupId]?.docs || [];
            
            // Collect all unique image URLs from expanded docs
            let variantImages = expandedDocs
              .map(doc => doc.image_url)
              .filter(Boolean);
            
            // Fallback: If no expanded docs, search for other products with same group
            if (variantImages.length === 0 && result.documents) {
              const sameGroupProducts = result.documents.filter(p => p.group === groupId && p.id !== product.id);
              variantImages = sameGroupProducts
                .map(p => p.image_url)
                .filter(Boolean);
            }
            
            // Always include the main product image as first variant
            if (product.image_url && !variantImages.includes(product.image_url)) {
              variantImages.unshift(product.image_url);
            }
            
            return {
              ...product,
              variant_images: variantImages
            };
          });
          setProducts(productsWithVariants);
        } else {
          setError(result.error || 'Failed to load featured products.');
        }
      } catch (err) {
        console.error('[FeaturedProducts] API call failed:', err);
        if (mountedRef.current) {
          setError(err.message || 'Failed to load featured products.');
        }
      } finally {
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    };
    
    loadFeaturedProducts();
      
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const renderProducts = () => {
    if (loading) {
      // Show skeleton cards while loading
      return Array.from({ length: 8 }).map((_, index) => (
        <Grid item xs={6} sm={6} md={3} key={`skeleton-${index}`}>
          <FeaturedProductSkeleton />
        </Grid>
      ));
    }

    if (!products || products.length === 0) {
      if (!error) {
        return (
          <Grid item xs={12}>
            <Typography variant="body1" align="center">
              No featured products found. This might be due to API configuration or data availability.
            </Typography>
          </Grid>
        );
      }
      return null;
    }

    return products.map((product, idx) => {
      if (!product) return null;
      
      return (
        <Grid 
          item 
          xs={6} 
          sm={6} 
          md={3} 
          key={product.id ? String(product.id) : product.name ? `name-${product.name}` : `product-${idx}`}
        >
          <ProductCard product={product} />
        </Grid>
      );
    });
  };

  return (
    <Container maxWidth="lg" sx={{ my: { xs: 3, md: 6 } }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2,
        flexWrap: 'wrap'
      }}>
        <Typography 
          variant="h5" 
          component="h2" 
          sx={{ 
            fontWeight: 700,
            fontSize: { xs: '1.5rem', md: '1.75rem' }
          }}
        >
          Featured Products
        </Typography>
      </Box>
      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}
      <Grid container spacing={2}>
        {renderProducts()}
      </Grid>
    </Container>
  );
});

export default FeaturedProducts;
